import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { restoreFocus } from '../../../utils/focus-trap.util';
import { ToastService } from '../toast/toast.service';
import type { PageGuideSession } from './page-guide.model';
import { guideStepsForPath } from './page-guide.registry';
import { revealKindForGuideTarget } from './page-guide-reveal';
import { findGuideTarget } from './page-guide-target';

@Injectable({ providedIn: 'root' })
export class PageGuideService {
  private readonly sessionSubject = new BehaviorSubject<PageGuideSession | null>(null);
  readonly session$ = this.sessionSubject.asObservable();
  private returnFocus: HTMLElement | null = null;
  private revealer: ((target: string) => Promise<boolean>) | null = null;

  constructor(
    private readonly router: Router,
    private readonly toast: ToastService,
  ) {
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe(() => this.close());
  }

  get isOpen(): boolean {
    return this.sessionSubject.value != null;
  }

  /**
   * The map registers this so a closed popup or route mode can be opened for the tour.
   * The callback must not save, change a phase, or delete.
   */
  setRevealer(revealer: ((target: string) => Promise<boolean>) | null): void {
    this.revealer = revealer;
  }

  reveal(target: string): Promise<boolean> {
    const revealer = this.revealer;
    return revealer ? revealer(target) : Promise.resolve(false);
  }

  /** Start the guide for the URL that is open now. Missing targets are skipped. */
  start(): void {
    const defined = guideStepsForPath(this.router.url);
    const steps = defined.filter(
      (step) => !!findGuideTarget(step.target) || !!revealKindForGuideTarget(step.target),
    );
    if (steps.length === 0) {
      this.toast.showInfo('Pro tuhle obrazovku průvodce nemá co ukázat.');
      this.close();
      return;
    }
    const active = document.activeElement;
    this.returnFocus = active instanceof HTMLElement ? active : null;
    this.sessionSubject.next({ steps, index: 0 });
  }

  next(): void {
    const session = this.sessionSubject.value;
    if (!session) return;
    if (session.index >= session.steps.length - 1) {
      this.close();
      return;
    }
    this.sessionSubject.next({ steps: session.steps, index: session.index + 1 });
  }

  back(): void {
    const session = this.sessionSubject.value;
    if (!session || session.index <= 0) return;
    this.sessionSubject.next({ steps: session.steps, index: session.index - 1 });
  }

  close(): void {
    if (!this.sessionSubject.value && !this.returnFocus) return;
    this.sessionSubject.next(null);
    const back = this.returnFocus;
    this.returnFocus = null;
    restoreFocus(back);
  }
}
